/**
 * Register Page — Unified progressive signup
 *
 * Step 1: single "Email or Phone" input.
 *   type=email  → password + confirm + name + T&C → POST /auth/register
 *   type=phone  → OTP sent to WhatsApp
 *                 → OTP input
 *                 → name + T&C
 *                 → POST /auth/otp/verify (with signup fields)
 *
 * Query param `?identifier=…` prefills the first input (used by the
 * Login page when it redirects an unknown identifier here).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, User, Loader2, ArrowLeft, KeyRound, Phone } from 'lucide-react';
import { useLogoUrl } from '../components/SAMALogo';

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function detectKind(value) {
  const v = (value || '').trim();
  if (!v) return { kind: 'unknown', normalized: '' };
  if (EMAIL_RE.test(v)) return { kind: 'email', normalized: v.toLowerCase() };
  const digits = v.replace(/\D+/g, '');
  const phone = digits.length > 10 && digits.startsWith('91') ? digits.slice(2) : digits;
  if (phone.length === 10) return { kind: 'phone', normalized: phone };
  return { kind: 'unknown', normalized: v };
}

const SAMALogo = () => {
  const logoUrl = useLogoUrl();
  return (
    <div className="flex items-center justify-center">
      <img src={logoUrl} alt="SAMA" className="h-32 md:h-36 w-auto object-contain" style={{ imageRendering: '-webkit-optimize-contrast' }} />
    </div>
  );
};

// Common privacy-consent + name block used by both flows
function DetailsBlock({ name, setName, accepted, setAccepted, acceptedMedical, setAcceptedMedical, studios, studioId, setStudioId }) {
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="name" className="text-sm font-medium">Full Name</Label>
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
          <Input id="name" autoFocus value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Your full name" className="sama-input h-14" style={{ paddingLeft: '3rem' }}
            required data-testid="name-input" />
        </div>
      </div>
      {studios.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="studio" className="text-sm font-medium">Preferred studio (optional)</Label>
          <select id="studio" value={studioId} onChange={(e) => setStudioId(e.target.value)}
            className="sama-input h-14 w-full px-4" data-testid="studio-select">
            <option value="">Select a studio</option>
            {studios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}

      {/* Mandatory medical acknowledgement — must be checked BEFORE the
          Privacy Policy checkbox per the studio's signup flow spec.
          Long copy so students know exactly what they're consenting to. */}
      <label className="flex items-start gap-3 cursor-pointer text-sm">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 accent-primary flex-shrink-0"
          checked={acceptedMedical}
          onChange={(e) => setAcceptedMedical(e.target.checked)}
          data-testid="medical-consent-checkbox"
        />
        <span className="text-muted-foreground text-[13px] leading-relaxed">
          We take utmost care to ensure a safe practice for everyone.
          Please note that if you suffer from any serious back/cervical
          issues, heart conditions, vertigo or have undergone any recent
          surgery or are under long term medications, it is assumed that
          you have appropriate medical clearance before attending a class
          with SAMA: The Calm Wellness Studio. The Studio does not take
          any responsibility for any pre-existing conditions getting
          triggered or aggravated during the practice. We also collect
          and use your phone number and email address to send you
          notifications regarding your bookings and other Studio
          updates. <span className="text-foreground font-medium">By checking this box, you agree with the above.</span>
        </span>
      </label>

      <label className="flex items-start gap-3 cursor-pointer text-sm">
        <input type="checkbox" className="mt-1 h-4 w-4 accent-primary" checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)} data-testid="privacy-checkbox" />
        <span className="text-muted-foreground">
          I agree to the{' '}
          <Link to="/privacy-policy" target="_blank" className="text-primary hover:underline">
            Privacy Policy
          </Link>
        </span>
      </label>
    </>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState('identifier'); // identifier|email-details|otp|otp-details
  const [identifier, setIdentifier] = useState(searchParams.get('identifier') || '');
  const [kind, setKind] = useState('unknown');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [studioId, setStudioId] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [acceptedMedical, setAcceptedMedical] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [studios, setStudios] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/public/studios`).then(r => r.json()).then(d => setStudios(d || [])).catch(() => {});
  }, []);

  // If the URL comes in with a prefilled identifier, auto-advance to
  // the right sub-step so the user doesn't repeat their input.
  useEffect(() => {
    const pre = searchParams.get('identifier');
    if (!pre) return;
    const { kind: k, normalized } = detectKind(pre);
    if (k === 'email') {
      setKind('email'); setIdentifier(normalized); setStep('email-details');
    } else if (k === 'phone') {
      setKind('phone'); setIdentifier(normalized);
      // Fire OTP silently; user goes straight to OTP entry.
      (async () => {
        try {
          const r = await fetch(`${API}/api/auth/otp/request`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            credentials: 'include', body: JSON.stringify({ identifier: normalized }),
          });
          const d = await r.json().catch(() => ({}));
          if (r.ok) { toast.success('OTP sent via WhatsApp'); setStep('otp'); }
          else toast.error(d.detail || 'Could not send OTP');
        } catch (e) { toast.error('Could not send OTP'); }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleIdentifierSubmit(e) {
    e.preventDefault();
    const { kind: k, normalized } = detectKind(identifier);
    if (k === 'unknown') return toast.error('Enter a valid email or 10-digit phone number');
    setIsLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/identifier-lookup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ identifier: normalized }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Lookup failed');
      if (data.exists) {
        toast.error(
          data.type === 'email'
            ? 'This email is already registered. Please login.'
            : 'This phone number is already registered. Please login.'
        );
        navigate(`/login?identifier=${encodeURIComponent(data.identifier)}`);
        return;
      }
      setKind(data.type);
      setIdentifier(data.identifier);
      if (data.type === 'email') {
        setStep('email-details');
      } else {
        const rr = await fetch(`${API}/api/auth/otp/request`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'include', body: JSON.stringify({ identifier: data.identifier }),
        });
        const dd = await rr.json().catch(() => ({}));
        if (!rr.ok) throw new Error(dd.detail || 'Could not send OTP');
        toast.success('OTP sent via WhatsApp');
        setStep('otp');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  }

  // ─── Email signup submit ──────────────────────────────────────
  async function handleEmailSignup(e) {
    e.preventDefault();
    if (password.length < 6) return toast.error('Password must be at least 6 characters');
    if (password !== confirmPassword) return toast.error('Passwords do not match');
    if (!name.trim()) return toast.error('Full name is required');
    if (!acceptedMedical) return toast.error('You must acknowledge the medical & notifications disclaimer to continue');
    if (!accepted) return toast.error('You must agree to the Privacy Policy to continue');
    setIsLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: identifier,
          password,
          name: name.trim(),
          preferred_studio_id: studioId || null,
          accepted_privacy: true,
          accepted_medical_disclaimer: true,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Signup failed');
      await login(data);
      toast.success('Welcome to SAMA!');
      navigate('/account-preferences', { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  }

  // ─── OTP submit (phone) ─────────────────────────────────────
  async function handleOtpSubmit(e) {
    e.preventDefault();
    if (!/^\d{4}$/.test(otp)) return toast.error('Enter the 4-digit OTP');
    // Move to the details sub-step where we collect name + T&C.
    // We hold onto the OTP; the verify call happens on the final submit
    // so the OTP isn't burned if the user quits at the details step.
    setStep('otp-details');
  }

  async function handleOtpDetailsSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return toast.error('Full name is required');
    if (!acceptedMedical) return toast.error('You must acknowledge the medical & notifications disclaimer to continue');
    if (!accepted) return toast.error('You must agree to the Privacy Policy to continue');
    setIsLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/otp/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          identifier,
          otp,
          name: name.trim(),
          preferred_studio_id: studioId || null,
          accepted_privacy: true,
          accepted_medical_disclaimer: true,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'OTP verification failed');
      await login(data);
      toast.success('Welcome to SAMA!');
      navigate('/account-preferences', { replace: true });
    } catch (err) {
      toast.error(err.message);
      // If OTP expired, drop them back to the identifier step so they
      // request a new one.
      if (/expired|not found/i.test(err.message)) setStep('identifier');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResendOtp() {
    setIsLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/otp/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ identifier }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Resend failed');
      toast.success('OTP re-sent');
    } catch (err) { toast.error(err.message); }
    finally { setIsLoading(false); }
  }

  const goBack = () => {
    if (step === 'identifier') { navigate('/'); return; }
    if (step === 'otp-details') { setStep('otp'); return; }
    setStep('identifier');
    setOtp(''); setPassword(''); setConfirmPassword('');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="relative z-10 p-6">
        <button onClick={goBack} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10"><SAMALogo /></div>
          <div className="text-center mb-8">
            <h1 className="text-2xl font-heading">Create your account</h1>
            <p className="text-muted-foreground mt-1">Start your journey with SAMA</p>
          </div>

          {step === 'identifier' && (
            <form onSubmit={handleIdentifierSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="identifier" className="text-sm font-medium">Phone or Email</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                  <Input id="identifier" autoFocus placeholder="9876543210 or you@example.com"
                    className="sama-input h-14" style={{ paddingLeft: '3rem' }}
                    value={identifier} onChange={(e) => setIdentifier(e.target.value)} required
                    data-testid="identifier-input" />
                </div>
              </div>
              <Button type="submit" className="w-full sama-btn-primary h-14 text-base" disabled={isLoading} data-testid="identifier-submit-btn">
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue'}
              </Button>
            </form>
          )}

          {step === 'email-details' && (
            <form onSubmit={handleEmailSignup} className="space-y-5">
              <div className="bg-secondary/40 rounded-xl px-4 py-3 text-sm text-muted-foreground flex items-center justify-between">
                <span className="flex items-center gap-2 truncate">
                  <Mail className="w-4 h-4 text-primary" /><span data-testid="identifier-echo">{identifier}</span>
                </span>
                <button type="button" onClick={goBack} className="text-primary text-xs font-medium">Change</button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                  <Input id="password" type="password" placeholder="••••••••" className="sama-input h-14"
                    style={{ paddingLeft: '3rem' }} value={password}
                    onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="password-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-sm font-medium">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                  <Input id="confirm" type="password" placeholder="••••••••" className="sama-input h-14"
                    style={{ paddingLeft: '3rem' }} value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="confirm-password-input" />
                </div>
              </div>
              <DetailsBlock name={name} setName={setName} accepted={accepted} setAccepted={setAccepted} acceptedMedical={acceptedMedical} setAcceptedMedical={setAcceptedMedical}
                studios={studios} studioId={studioId} setStudioId={setStudioId} />
              <Button type="submit" className="w-full sama-btn-primary h-14 text-base" disabled={isLoading} data-testid="register-submit-btn">
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
              </Button>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleOtpSubmit} className="space-y-5">
              <div className="bg-secondary/40 rounded-xl px-4 py-3 text-sm text-muted-foreground flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary" /><span data-testid="identifier-echo">+91 {identifier}</span>
                </span>
                <button type="button" onClick={goBack} className="text-primary text-xs font-medium">Change</button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-sm font-medium">Enter 4-digit OTP sent on WhatsApp</Label>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                  <Input id="otp" inputMode="numeric" autoComplete="one-time-code" maxLength={4} autoFocus
                    placeholder="0000" className="sama-input h-14 tracking-[0.4em] text-center text-lg"
                    value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    required data-testid="otp-input" />
                </div>
              </div>
              <Button type="submit" className="w-full sama-btn-primary h-14 text-base" disabled={otp.length !== 4} data-testid="otp-submit-btn">
                Verify OTP
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Didn&apos;t get it?{' '}
                <button type="button" onClick={handleResendOtp} className="text-primary font-medium hover:underline" data-testid="otp-resend-btn">
                  Resend OTP
                </button>
              </p>
            </form>
          )}

          {step === 'otp-details' && (
            <form onSubmit={handleOtpDetailsSubmit} className="space-y-5">
              <div className="bg-secondary/40 rounded-xl px-4 py-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary" />+91 {identifier} <span className="text-primary">· verified</span>
                </span>
              </div>
              <DetailsBlock name={name} setName={setName} accepted={accepted} setAccepted={setAccepted} acceptedMedical={acceptedMedical} setAcceptedMedical={setAcceptedMedical}
                studios={studios} studioId={studioId} setStudioId={setStudioId} />
              <Button type="submit" className="w-full sama-btn-primary h-14 text-base" disabled={isLoading} data-testid="register-submit-btn">
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
              </Button>
            </form>
          )}

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
